// src/pages/OrderLookup.tsx

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import API_BASE_URL from '../utils/apiBase';

interface DownloadToken {
  pdfTitle: string;
  token: string;
  downloadCount: number;
  maxDownloads: number;
  expiresAt: string;
  downloadUrl: string;
}

interface OrderData {
  order: {
    orderNumber: string;
    totalAmount: number;
    currency: string;
    createdAt: string;
    items: Array<{
      id: number;
      title: string;
      quantity: number;
      price?: number;
    }>;
  };
  downloads: DownloadToken[];
}

const OrderLookup: React.FC = () => {
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orderData, setOrderData] = useState<OrderData | null>(null);

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setOrderData(null);

    try {
      const response = await fetch(`${API_BASE_URL}/orders/lookup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          orderNumber: orderNumber.trim(),
          email: email.trim(),
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Order not found');
      }

      const data = await response.json();
      setOrderData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to find order');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (downloadUrl: string) => {
    window.location.href = `${API_BASE_URL}${downloadUrl}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const daysUntilExpiry = (expiresAt: string) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const days = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return days;
  };

  return (
    <div className="section container">
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <h1 style={{ marginBottom: '1rem' }}>Order Lookup</h1>
        <p className="text-secondary" style={{ marginBottom: '2rem' }}>
          Enter your order number and email to view and download your purchased files.
        </p>

        {/* Lookup Form */}
        {!orderData && (
          <div className="card" style={{ padding: '2rem', marginBottom: '2rem' }}>
            <form onSubmit={handleLookup}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="orderNumber" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Order Number
                </label>
                <input
                  type="text"
                  id="orderNumber"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  placeholder="ORD-XXXXXXXXXX"
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '1rem',
                  }}
                />
                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                  Found in your order confirmation email
                </p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="email" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '1rem',
                  }}
                />
              </div>

              {error && (
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  marginBottom: '1.5rem',
                }}>
                  <p style={{ color: '#dc2626', margin: 0 }}>{error}</p>
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
                style={{ width: '100%' }}
              >
                {loading ? 'Looking up order...' : 'View My Order'}
              </button>
            </form>
          </div>
        )}

        {/* Order Details */}
        {orderData && (
          <>
            <div className="card" style={{ padding: '2rem', marginBottom: '2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
                <div>
                  <h2 style={{ margin: '0 0 0.5rem 0' }}>Order #{orderData.order.orderNumber}</h2>
                  <p className="text-secondary" style={{ margin: 0 }}>
                    Placed on {formatDate(orderData.order.createdAt)}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setOrderData(null);
                    setOrderNumber('');
                    setEmail('');
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #d1d5db',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                  }}
                >
                  Look up different order
                </button>
              </div>

              <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1.5rem' }}>
                <h3 style={{ marginBottom: '1rem' }}>Order Total</h3>
                <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--color-primary)', margin: 0 }}>
                  ${orderData.order.totalAmount.toFixed(2)} {orderData.order.currency.toUpperCase()}
                </p>
              </div>
            </div>

            {/* Download Links */}
            <h3 style={{ marginBottom: '1rem' }}>Your Files</h3>
            {orderData.downloads.map((download) => {
              const daysLeft = daysUntilExpiry(download.expiresAt);
              const downloadsLeft = download.maxDownloads - download.downloadCount;

              return (
                <div key={download.token} className="card" style={{ padding: '1.5rem', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ margin: '0 0 0.5rem 0' }}>{download.pdfTitle}</h4>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        <p style={{ margin: '0 0 0.25rem 0' }}>
                          📥 {downloadsLeft} download{downloadsLeft !== 1 ? 's' : ''} remaining
                        </p>
                        <p style={{ margin: 0 }}>
                          ⏰ Expires in {daysLeft} day{daysLeft !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={() => handleDownload(download.downloadUrl)}
                      className="btn btn-primary"
                      disabled={downloadsLeft === 0 || daysLeft < 0}
                    >
                      {downloadsLeft === 0 ? 'Download Limit Reached' : daysLeft < 0 ? 'Expired' : 'Download PDF'}
                    </button>
                  </div>

                  {(downloadsLeft === 0 || daysLeft < 0) && (
                    <div style={{
                      marginTop: '1rem',
                      padding: '0.75rem',
                      backgroundColor: '#fef3c7',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                    }}>
                      <p style={{ margin: 0 }}>
                        ⚠️ This download link is no longer available. Please contact support if you need assistance.
                      </p>
                    </div>
                  )}
                </div>
              );
            })}

            <div style={{ marginTop: '2rem', textAlign: 'center' }}>
              <Link to="/pdf" className="btn" style={{ backgroundColor: 'white', border: '1px solid #d1d5db' }}>
                Browse More PDFs
              </Link>
            </div>
          </>
        )}

        {/* Help Section */}
        <div className="card" style={{ padding: '1.5rem', backgroundColor: '#f9fafb', marginTop: '2rem' }}>
          <h4 style={{ margin: '0 0 1rem 0' }}>Need Help?</h4>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0 0 1rem 0' }}>
            If you're having trouble accessing your files or can't find your order number, please contact us:
          </p>
          <p style={{ fontSize: '0.875rem', margin: 0 }}>
            <strong>Email:</strong> support@snowbirdindustries.com
          </p>
        </div>
      </div>
    </div>
  );
};

export default OrderLookup;
